import picaFactory from 'pica';

export type OutputFormat = 'jpeg' | 'png' | 'webp' | 'avif';
export type Transform = {
  format: OutputFormat;
  quality: number;
  width?: number;
  height?: number;
  crop?: { x: number; y: number; width: number; height: number };
  rotate?: 0 | 90 | 180 | 270;
  flipX?: boolean;
  flipY?: boolean;
  background?: string;
  targetBytes?: number;
};

export type Decoded = { source: CanvasImageSource; width: number; height: number; dispose: () => void; hasAlpha: boolean };

const mimeFor = (format: OutputFormat) => `image/${format}`;
export const extensionFor = (format: OutputFormat) => format === 'jpeg' ? 'jpg' : format;

export function normaliseFormat(type: string, name = ''): string {
  if (/heic|heif/i.test(type) || /\.(heic|heif)$/i.test(name)) return 'heic';
  if (/avif/i.test(type) || /\.avif$/i.test(name)) return 'avif';
  if (/png/i.test(type) || /\.png$/i.test(name)) return 'png';
  if (/webp/i.test(type) || /\.webp$/i.test(name)) return 'webp';
  if (/jpe?g/i.test(type) || /\.jpe?g$/i.test(name)) return 'jpeg';
  return 'unknown';
}

export async function isAnimated(file: File): Promise<boolean> {
  const bytes = new Uint8Array(await file.slice(0, Math.min(file.size, 2_000_000)).arrayBuffer());
  const format = normaliseFormat(file.type, file.name);
  if (format === 'webp') return bytes.length > 21 && String.fromCharCode(...bytes.slice(12, 16)) === 'VP8X' && (bytes[20] & 2) !== 0;
  if (format === 'png') {
    for (let i = 8; i + 8 < bytes.length; i++) if (bytes[i] === 0x61 && bytes[i+1] === 0x63 && bytes[i+2] === 0x54 && bytes[i+3] === 0x4c) return true;
  }
  if (/gif/i.test(file.type) || /\.gif$/i.test(file.name)) {
    let frames = 0;
    for (let i = 0; i < bytes.length; i++) if (bytes[i] === 0x2c && ++frames > 1) return true;
  }
  return false;
}

export async function decodeImage(file: File): Promise<Decoded> {
  if (await isAnimated(file)) throw new Error('Animated images are not changed because that would discard animation.');
  const format = normaliseFormat(file.type, file.name);
  if (format === 'unknown') throw new Error('This file format is not supported. Use JPG, PNG, WebP, HEIC or AVIF.');
  if (format === 'heic') {
    const { decode } = await import('@discourse/heic');
    const image = await decode(await file.arrayBuffer());
    const bitmap = await createImageBitmap(image);
    return { source: bitmap, width: bitmap.width, height: bitmap.height, dispose: () => bitmap.close(), hasAlpha: false };
  }
  if (format === 'avif') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, dispose: () => bitmap.close(), hasAlpha: true };
    } catch {
      const { default: decode } = await import('@jsquash/avif/decode.js');
      const image = await decode(await file.arrayBuffer());
      if (!image) throw new Error('This AVIF image could not be decoded.');
      const bitmap = await createImageBitmap(image);
      return { source: bitmap, width: bitmap.width, height: bitmap.height, dispose: () => bitmap.close(), hasAlpha: true };
    }
  }
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  return { source: bitmap, width: bitmap.width, height: bitmap.height, dispose: () => bitmap.close(), hasAlpha: format === 'png' || format === 'webp' };
}

function canvas(width: number, height: number) {
  const el = document.createElement('canvas');
  el.width = Math.max(1, Math.round(width)); el.height = Math.max(1, Math.round(height));
  return el;
}

async function toBlob(el: HTMLCanvasElement, format: OutputFormat, quality: number): Promise<Blob> {
  if (format === 'avif') {
    const ctx = el.getContext('2d', { willReadFrequently: true })!;
    const image = ctx.getImageData(0, 0, el.width, el.height);
    const { default: encode } = await import('@jsquash/avif/encode.js');
    const encoded = await encode(image, { quality: Math.round(quality * 100), qualityAlpha: Math.round(quality * 100), bitDepth: 8 });
    return new Blob([encoded], { type: 'image/avif' });
  }
  return new Promise((resolve, reject) => el.toBlob(blob => blob ? resolve(blob) : reject(new Error(`Your browser cannot create ${format.toUpperCase()} files.`)), mimeFor(format), quality));
}

async function draw(decoded: Decoded, options: Transform, scale = 1): Promise<HTMLCanvasElement> {
  const crop = options.crop || { x: 0, y: 0, width: decoded.width, height: decoded.height };
  if (crop.x < 0 || crop.y < 0 || crop.width < 1 || crop.height < 1 || crop.x + crop.width > decoded.width || crop.y + crop.height > decoded.height) {
    throw new Error(`Crop must stay inside the ${decoded.width} × ${decoded.height} image.`);
  }
  const baseWidth = options.width || (options.height ? crop.width * options.height / crop.height : crop.width);
  const baseHeight = options.height || (options.width ? crop.height * options.width / crop.width : crop.height);
  const width = Math.max(1, Math.round(baseWidth * scale));
  const height = Math.max(1, Math.round(baseHeight * scale));
  const rotated = options.rotate === 90 || options.rotate === 270;
  const result = canvas(rotated ? height : width, rotated ? width : height);
  const intermediate = canvas(crop.width, crop.height);
  const ictx = intermediate.getContext('2d')!;
  ictx.drawImage(decoded.source, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);
  const resized = canvas(width, height);
  if (width === crop.width && height === crop.height) resized.getContext('2d')!.drawImage(intermediate, 0, 0);
  else await picaFactory().resize(intermediate, resized, { quality: 3 });
  const ctx = result.getContext('2d')!;
  if (options.format === 'jpeg') { ctx.fillStyle = options.background || '#ffffff'; ctx.fillRect(0, 0, result.width, result.height); }
  ctx.translate(result.width / 2, result.height / 2);
  ctx.rotate((options.rotate || 0) * Math.PI / 180);
  ctx.scale(options.flipX ? -1 : 1, options.flipY ? -1 : 1);
  ctx.drawImage(resized, -width / 2, -height / 2, width, height);
  return result;
}

export async function transformImage(file: File, options: Transform): Promise<{ blob: Blob; width: number; height: number; quality: number }> {
  const decoded = await decodeImage(file);
  try {
    if (!options.targetBytes) {
      const output = await draw(decoded, options);
      const blob = await toBlob(output, options.format, options.quality);
      return { blob, width: output.width, height: output.height, quality: options.quality };
    }
    const target = options.targetBytes;
    let scale = 1;
    let best: { blob: Blob; width: number; height: number; quality: number } | null = null;
    for (let resizePass = 0; resizePass < 8; resizePass++) {
      const output = await draw(decoded, options, scale);
      let low = 0.03, high = Math.min(options.quality, 0.98);
      for (let pass = 0; pass < 10; pass++) {
        const quality = (low + high) / 2;
        const blob = await toBlob(output, options.format, quality);
        if (blob.size <= target) { best = { blob, width: output.width, height: output.height, quality }; low = quality; }
        else high = quality;
      }
      if (best) return best;
      const smallest = await toBlob(output, options.format, 0.02);
      const ratio = Math.sqrt(target / Math.max(smallest.size, 1)) * 0.92;
      scale *= Math.min(0.85, Math.max(0.35, ratio));
      if (output.width <= 64 || output.height <= 64) break;
    }
    throw new Error('This image cannot fit the target without becoming unusably small. Try a larger target.');
  } finally { decoded.dispose(); }
}

export async function inspectImage(file: File) {
  const decoded = await decodeImage(file);
  const info = { width: decoded.width, height: decoded.height, format: normaliseFormat(file.type, file.name) };
  decoded.dispose();
  return info;
}
